import { inject } from '@angular/core'
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { PlayersManagerApi } from '../players-manager.api'
import { PagingService } from '@core/services/paging/paging.service'
import { catchRequestError } from '@performance/ui'
import { ToastService } from '@core/components/toast/toast.service'
import { ToastType } from '@core/components/toast/toast.models'
import { LazyParams } from '@shared/components/table/table.models'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'

export const PlayersManagerStore = signalStore(
  withState({
    playersData: null as any,
  }),
  withMethods((store) => {
    const api = inject(PlayersManagerApi)
    const pagingService = inject(PagingService)
    const toastService = inject(ToastService)

    return {
      fetchPlayersData: rxMethod<{ params: any; lazyParams: LazyParams }>((source$) =>
        source$.pipe(
          switchMap(({ params, lazyParams }) =>
            api
              .fetchPlayers$(params, {
                paging: pagingService.getParams(),
                ...lazyParams,
              })
              .pipe(
                map((playersData) => ({
                  ...playersData,
                  items: playersData?.players.map((player) => ({
                    ...player,
                    id: player.playerId,
                    mcSpecificPosition: player?.position?.specificPosition || ' - ',
                    mcPositionId: player?.position?.id || null,
                    inscriptionTroubleKey: player.hasLaLigaInscriptionTrouble
                      ? 'MTR_COMMON_PLAYER_INSCRIPTION_TROUBLE_TRUE'
                      : 'MTR_COMMON_PLAYER_INSCRIPTION_TROUBLE_FALSE',
                  })),
                })),
                tap((playersData: Record<string, any>) =>
                  patchState(store, { playersData: { ...playersData } }),
                ),
                catchError((err) => {
                  console.error('Unable to fetch players data', err)
                  return of(undefined)
                }),
              ),
          ),
        ),
      ),
      uploadFiles: rxMethod<{ files: any[]; season: string }>((source$) =>
        source$.pipe(
          switchMap(({ files, season }) => {
            const formData = new FormData()
            files.forEach((file) => formData.append('files', file, file.name))
            return api.uploadPlayerFiles$(formData, season).pipe(
              tap(() =>
                toastService.showToast({
                  type: ToastType.Success,
                  message: 'MTR_COMMON_PLAYER_FILE_UPLOAD_SUCCESS_MESSAGE',
                }),
              ),
              catchRequestError(),
              catchError(() => of(undefined)),
            )
          }),
        ),
      ),
    }
  }),
)
